// Accessibility settings — colour-blind palettes, dyslexic font, high
// contrast, subliminal highlight, Morse & Braille vibration for blind users.
// All settings persist offline via persistOptions.
import { getOption, setOption, subscribeOptions } from "./persistOptions";

export type ColourMode = "default" | "protanopia" | "deuteranopia" | "tritanopia" | "high-contrast" | "sepia";
export type Vibration = "off" | "morse" | "braille";

export interface A11ySettings {
  colourMode: ColourMode;
  textScale: number;          // 1 = 100%
  dyslexic: boolean;
  reduceMotion: boolean;
  highlightKey: boolean;      // subliminal colour highlight on key words
  vibration: Vibration;
  readAloud: boolean;
  ttsRate: number;            // 0.5..1.6
}

const KEY = "sophia:a11y";
const DEFAULT: A11ySettings = {
  colourMode: "default", textScale: 1, dyslexic: false, reduceMotion: false,
  highlightKey: true, vibration: "off", readAloud: false, ttsRate: 0.98,
};

export function getA11y(): A11ySettings {
  return { ...DEFAULT, ...getOption<Partial<A11ySettings>>(KEY, {}) };
}
export function setA11y(patch: Partial<A11ySettings>) {
  setOption(KEY, { ...getA11y(), ...patch });
  applyA11y();
}
export function subscribeA11y(fn: (s: A11ySettings) => void): () => void {
  return subscribeOptions(() => fn(getA11y()));
}

/** Apply CSS variables + document classes so every screen picks it up. */
export function applyA11y() {
  if (typeof document === "undefined") return;
  const s = getA11y();
  const root = document.documentElement;
  root.style.setProperty("--a11y-scale", String(s.textScale));
  root.style.fontSize = `${16 * s.textScale}px`;
  root.dataset.colourMode = s.colourMode;
  root.classList.toggle("a11y-dyslexic", s.dyslexic);
  root.classList.toggle("a11y-reduce-motion", s.reduceMotion);
  root.classList.toggle("a11y-highlight", s.highlightKey);
}

// ---- Morse & Braille ------------------------------------------------------

const MORSE: Record<string, string> = {
  a: ".-", b: "-...", c: "-.-.", d: "-..", e: ".", f: "..-.", g: "--.", h: "....",
  i: "..", j: ".---", k: "-.-", l: ".-..", m: "--", n: "-.", o: "---", p: ".--.",
  q: "--.-", r: ".-.", s: "...", t: "-", u: "..-", v: "...-", w: ".--", x: "-..-",
  y: "-.--", z: "--..", "0": "-----", "1": ".----", "2": "..---", "3": "...--",
  "4": "....-", "5": ".....", "6": "-....", "7": "--...", "8": "---..", "9": "----.",
  ".": ".-.-.-", ",": "--..--", "?": "..--..", "!": "-.-.--", " ": "/",
};

// Braille 6-dot: dot positions 1..6, letters in a-j pattern + shifts.
const BRAILLE: Record<string, number[]> = {
  a: [1], b: [1, 2], c: [1, 4], d: [1, 4, 5], e: [1, 5], f: [1, 2, 4],
  g: [1, 2, 4, 5], h: [1, 2, 5], i: [2, 4], j: [2, 4, 5],
  k: [1, 3], l: [1, 2, 3], m: [1, 3, 4], n: [1, 3, 4, 5], o: [1, 3, 5],
  p: [1, 2, 3, 4], q: [1, 2, 3, 4, 5], r: [1, 2, 3, 5], s: [2, 3, 4], t: [2, 3, 4, 5],
  u: [1, 3, 6], v: [1, 2, 3, 6], w: [2, 4, 5, 6], x: [1, 3, 4, 6],
  y: [1, 3, 4, 5, 6], z: [1, 3, 5, 6], " ": [],
};

/** Convert text to a Morse vibration pattern (ms values for navigator.vibrate). */
export function toMorseVibration(text: string): number[] {
  const DOT = 90, DASH = 260, GAP = 90, LETTER = 240, WORD = 560;
  const out: number[] = [];
  for (const ch of text.toLowerCase()) {
    const code = MORSE[ch];
    if (!code) continue;
    if (code === "/") { out.push(0, WORD); continue; }
    for (let i = 0; i < code.length; i++) {
      out.push(code[i] === "." ? DOT : DASH);
      out.push(i === code.length - 1 ? LETTER : GAP);
    }
  }
  return out;
}

/** Braille vibration: 6 short pulses per cell, dot present = 60ms, absent = short pause. */
export function toBrailleVibration(text: string): number[] {
  const DOT = 70, EMPTY = 25, GAP = 60, CELL = 200;
  const out: number[] = [];
  for (const ch of text.toLowerCase()) {
    const cell = BRAILLE[ch];
    if (!cell) continue;
    for (let d = 1; d <= 6; d++) {
      out.push(cell.includes(d) ? DOT : 0);
      out.push(cell.includes(d) ? GAP : EMPTY);
    }
    out.push(0, CELL);
  }
  return out;
}

/** Send a haptic burst reading `text` via the user's chosen mode. */
export function vibrateText(text: string) {
  if (typeof navigator === "undefined" || !navigator.vibrate) return false;
  const mode = getA11y().vibration;
  if (mode === "off") return false;
  const pattern = mode === "morse" ? toMorseVibration(text) : toBrailleVibration(text);
  if (!pattern.length) return false;
  navigator.vibrate(pattern);
  return true;
}

export const MORSE_TABLE = MORSE;
export const BRAILLE_TABLE = BRAILLE;
